import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

/**
 * Generic CRUD admin page.
 * Props:
 *  - title: page title
 *  - resource: API path segment (e.g. "students")
 *  - columns: [{ key, label, format?(v, row) }]
 *  - fields: [{ key, label, type, options?, required? }]
 *  - defaults: object of default values
 */
const CrudPage = ({ title, resource, columns, fields, defaults, testId, extraRowActions }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | item
  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(false);

  const load = () => api.get(`/admin/${resource}`).then(r => setItems(r.data));
  useEffect(() => { load(); }, [resource]);

  const openNew = () => { setForm(defaults); setEditing("new"); };
  const openEdit = (it) => { setForm({ ...defaults, ...it }); setEditing(it); };
  const close = () => setEditing(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // strip non-field keys like id/_id, created_at
      const payload = {};
      fields.forEach(f => {
        let v = form[f.key];
        if (f.type === "number") v = v === "" || v == null ? 0 : Number(v);
        if (f.type === "boolean") v = !!v;
        if (f.type === "list") v = (typeof v === "string" ? v.split(",").map(x => x.trim()).filter(Boolean) : v) || [];
        payload[f.key] = v ?? defaults[f.key] ?? "";
      });
      if (editing === "new") {
        await api.post(`/admin/${resource}`, payload);
      } else {
        await api.put(`/admin/${resource}/${editing.id}`, payload);
      }
      toast.success("Gespeichert");
      close();
      load();
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally { setLoading(false); }
  };

  const remove = async (it) => {
    if (!window.confirm("Wirklich löschen?")) return;
    try {
      await api.delete(`/admin/${resource}/${it.id}`);
      toast.success("Gelöscht");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unbekannter Fehler";
      toast.error(`Löschen fehlgeschlagen: ${detail}`);
    }
  };

  return (
    <div className="space-y-6" data-testid={testId || `admin-${resource}`}>
      <Toaster position="top-center" theme="light" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif-display text-3xl">{title}</h1>
          <p className="text-sm text-muted-foreground">{items.length} Einträge</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 btn-gold px-5 py-2.5 rounded-full text-sm" data-testid={`${resource}-new-btn`}>
          <Plus className="w-4 h-4" /> Neu
        </button>
      </div>

      <div className="card-academy rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 border-b border-border">
              <tr>
                {columns.map(c => <th key={c.key} className="text-left text-xs uppercase tracking-[0.15em] text-muted-foreground px-4 py-3">{c.label}</th>)}
                <th className="px-4 py-3 text-right text-xs uppercase tracking-[0.15em] text-muted-foreground">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} className="border-b border-border/50 hover:bg-secondary/20">
                  {columns.map(c => (
                    <td key={c.key} className="px-4 py-3">
                      {c.format ? c.format(it[c.key], it) : (it[c.key] ?? "—")}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right">
                    {extraRowActions && extraRowActions(it)}
                    <button onClick={() => openEdit(it)} className="text-muted-foreground hover:text-primary mr-2"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(it)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-muted-foreground">Keine Einträge</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={close}>
          <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="font-serif-display text-xl">{editing === "new" ? "Neu" : "Bearbeiten"}</div>
              <button type="button" onClick={close} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              {fields.map(f => (
                <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
                  <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea rows={3} value={form[f.key] ?? ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                      className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary" />
                  ) : f.type === "select" ? (
                    <select value={form[f.key] ?? ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                      className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary">
                      <option value="">–</option>
                      {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : f.type === "boolean" ? (
                    <label className="inline-flex items-center gap-2 mt-2">
                      <input type="checkbox" checked={!!form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.checked })} className="accent-[hsl(35_90%_55%)]" />
                      <span className="text-sm">{form[f.key] ? "Ja" : "Nein"}</span>
                    </label>
                  ) : f.type === "list" ? (
                    <input value={Array.isArray(form[f.key]) ? form[f.key].join(", ") : (form[f.key] ?? "")}
                      onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                      placeholder="Komma-getrennt"
                      className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary" />
                  ) : (
                    <input type={f.type || "text"} value={form[f.key] ?? ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                      required={f.required}
                      className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary" />
                  )}
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-border flex justify-end gap-3">
              <button type="button" onClick={close} className="px-5 py-2 text-sm border border-border rounded-full hover:border-muted-foreground">Abbrechen</button>
              <button type="submit" disabled={loading} className="px-6 py-2 text-sm rounded-full btn-gold disabled:opacity-50">{loading ? "…" : "Speichern"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CrudPage;
